import {
  getAccountNumberError,
  getPhoneNumberError,
  normalizePhoneNumber,
  validateRoutingNumber,
} from '@steerfi/shared';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useRef, useState } from 'react';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import {
  type AddressFormData,
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FormControl,
  type FormErrorItem,
  FormErrorSummary,
  FormField,
  focusFirstInvalid,
} from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface IndividualRegistrationFormProps {
  onBack: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  // Address info
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  addressVerificationStatus: AddressVerificationStatus;
  // ACH info
  achAccountNumber: string;
  achRoutingNumber: string;
  achAccountType: 'checking' | 'savings';
  achBankName: string;
  achAccountHolderName: string;
}

interface FormErrors {
  [key: string]: string;
}

const REG_FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phoneNumber: 'Phone Number',
  password: 'Password',
  confirmPassword: 'Confirm Password',
  address1: 'Street Address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP Code',
  achAccountHolderName: 'Account Holder Name',
  achRoutingNumber: 'Routing Number',
  achAccountNumber: 'Account Number',
  general: 'Form',
};

function errorsToSummaryItems(errors: FormErrors): FormErrorItem[] {
  return Object.entries(errors)
    .filter(([key]) => key !== 'general')
    .map(([key, message]) => ({
      label: REG_FIELD_LABELS[key] ?? key,
      message,
    }));
}

export function IndividualRegistrationForm({ onBack }: IndividualRegistrationFormProps) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    addressVerificationStatus: 'not_checked',
    achAccountNumber: '',
    achRoutingNumber: '',
    achAccountType: 'checking',
    achBankName: '',
    achAccountHolderName: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Personal Info + Address, 2: ACH Info

  // Address verification state
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressVerificationResult, setAddressVerificationResult] =
    useState<AddressVerificationResponse | null>(null);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle address field changes
  const handleAddressChange = (field: keyof AddressFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset verification status when address changes
    if (formData.addressVerificationStatus !== 'not_checked') {
      setFormData((prev) => ({ ...prev, addressVerificationStatus: 'not_checked' }));
    }
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Handle address verification result
  const handleAddressVerificationResult = (result: AddressVerificationResponse) => {
    setAddressVerificationResult(result);
    // Show dialog if there are corrections or if verification failed
    if (result.hasCorrections || !result.verified) {
      setShowAddressDialog(true);
    }
  };

  // Handle accepting suggested address
  const handleAcceptSuggestedAddress = (suggested: StandardizedAddress) => {
    setFormData((prev) => ({
      ...prev,
      address1: suggested.streetAddress,
      address2: suggested.secondaryAddress || '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
      addressVerificationStatus: 'verified',
    }));
    setShowAddressDialog(false);
  };

  // Handle keeping original address
  const handleKeepOriginalAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addressVerificationStatus: addressVerificationResult?.verified ? 'verified' : 'not_verified',
    }));
    setShowAddressDialog(false);
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Enter a first name';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Enter a last name';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Enter an email like name@company.com';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter an email like name@company.com';
    }
    const phoneError = getPhoneNumberError(formData.phoneNumber);
    if (phoneError) {
      newErrors.phoneNumber = phoneError;
    }
    if (!formData.password) {
      newErrors.password = 'Enter a password with at least 8 characters';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Enter a password with at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Re-enter the same password in both fields';
    }

    // Address validation
    if (!formData.address1.trim()) {
      newErrors.address1 = 'Enter a street address';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'Enter a city';
    }
    if (!formData.state) {
      newErrors.state = 'Select a state';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'Enter a ZIP code';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Enter a 5-digit ZIP or ZIP+4 like 10001-1234';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.achAccountHolderName.trim()) {
      newErrors.achAccountHolderName = 'Enter the account holder name';
    }

    // Use improved routing number validation with detailed error messages
    const routingValidation = validateRoutingNumber(formData.achRoutingNumber);
    if (!routingValidation.isValid && routingValidation.message) {
      newErrors.achRoutingNumber = routingValidation.message;
    }

    // Use improved account number validation with detailed error messages
    const accountError = getAccountNumberError(formData.achAccountNumber);
    if (accountError) {
      newErrors.achAccountNumber = accountError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const returnToPersonalInfo = () => {
    setErrors({});
    setSubmitAttempt(0);
    setCurrentStep(1);
  };

  const handleNext = () => {
    if (validateStep1()) {
      setSubmitAttempt(0);
      setCurrentStep(2);
    } else {
      setSubmitAttempt((n) => n + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep2()) {
      setSubmitAttempt((n) => n + 1);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch('/api/auth/register/individual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: normalizePhoneNumber(formData.phoneNumber),
          password: formData.password,
          // Address fields
          address1: formData.address1.trim(),
          address2: formData.address2.trim() || null,
          city: formData.city.trim(),
          state: formData.state,
          zipCode: formData.zipCode,
          addressVerificationStatus: formData.addressVerificationStatus,
          // ACH fields
          achAccountNumber: formData.achAccountNumber,
          achRoutingNumber: formData.achRoutingNumber,
          achAccountType: formData.achAccountType,
          achBankName: formData.achBankName || null,
          achAccountHolderName: formData.achAccountHolderName,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Redirect to registration success page
        window.location.href = '/registration-success?registered=true';
      } else {
        // Handle 409 Conflict - email already exists
        if (response.status === 409) {
          const message = data.message?.toLowerCase() || '';
          if (message.includes('email') || message.includes('account')) {
            setErrors({
              email: 'An account with this email already exists. Please log in instead.',
            });
            // Navigate back to step 1 where the email field is, and treat this
            // like any other failed submit: announce it and focus Email.
            setCurrentStep(1);
            setSubmitAttempt((n) => n + 1);
            return;
          }
        }

        // Handle specific error cases
        if (response.status === 501 || data.message?.includes('not yet implemented')) {
          setErrors({
            general:
              'Individual registration is currently not available. Please use business registration or contact support for assistance.',
          });
        } else {
          setErrors({ general: data.message || 'Registration failed' });
        }
        setSubmitAttempt((n) => n + 1);
      }
    } catch (error) {
      setErrors({
        general: `An error occurred during registration. Please try again.  ${error instanceof Error ? error.message : ''}`,
      });
      setSubmitAttempt((n) => n + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={currentStep === 1 ? onBack : returnToPersonalInfo}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle as="h1" className="text-2xl">
              Individual Registration
            </CardTitle>
            <CardDescription>
              {currentStep === 1
                ? 'Step 1 of 2: Personal Information and Address'
                : 'Step 2 of 2: ACH Account Information'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" noValidate>
              <FormErrorSummary
                errors={errorsToSummaryItems(errors)}
                attempt={submitAttempt}
                onAnnounced={() => focusFirstInvalid(formRef.current)}
              />
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField label="First Name" required error={errors.firstName}>
                      <FormControl>
                        <Input
                          placeholder="John"
                          value={formData.firstName}
                          onChange={(e) => handleChange('firstName', e.target.value)}
                        />
                      </FormControl>
                    </FormField>

                    <FormField label="Last Name" required error={errors.lastName}>
                      <FormControl>
                        <Input
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={(e) => handleChange('lastName', e.target.value)}
                        />
                      </FormControl>
                    </FormField>
                  </div>

                  <FormField label="Email" required error={errors.email}>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="john.doe@example.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                      />
                    </FormControl>
                  </FormField>

                  <FormField label="Phone Number" required error={errors.phoneNumber}>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="+1 234 567 8900"
                        value={formData.phoneNumber}
                        onChange={(e) => handleChange('phoneNumber', e.target.value)}
                      />
                    </FormControl>
                  </FormField>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      label="Password"
                      required
                      error={errors.password}
                      description="At least 8 characters"
                    >
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={(e) => handleChange('password', e.target.value)}
                            className="pr-10"
                          />
                        </FormControl>
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormField>

                    <FormField label="Confirm Password" required error={errors.confirmPassword}>
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={formData.confirmPassword}
                            onChange={(e) => handleChange('confirmPassword', e.target.value)}
                            className="pr-10"
                          />
                        </FormControl>
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label={
                            showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormField>
                  </div>

                  {/* Address Section */}
                  <div className="pt-4 border-t">
                    <h2 className="text-lg font-medium mb-4">Your Address</h2>
                    <AddressFormFields
                      address={{
                        address1: formData.address1,
                        address2: formData.address2,
                        city: formData.city,
                        state: formData.state,
                        zipCode: formData.zipCode,
                      }}
                      errors={errors}
                      onChange={handleAddressChange}
                      onVerificationResult={handleAddressVerificationResult}
                      onVerificationStatusChange={(status) =>
                        setFormData((prev) => ({ ...prev, addressVerificationStatus: status }))
                      }
                    />
                  </div>

                  <Button type="button" className="w-full" size="lg" onClick={handleNext}>
                    Next: ACH Information
                  </Button>
                </div>
              )}

              {/* Step 2: ACH Account Information */}
              {currentStep === 2 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <Alert>
                    <AlertDescription>
                      Your ACH account information will be encrypted and stored securely. This
                      information is required for payment processing.
                    </AlertDescription>
                  </Alert>

                  <FormField
                    label="Account Holder Name"
                    required
                    error={errors.achAccountHolderName}
                  >
                    <FormControl>
                      <Input
                        placeholder="John Doe"
                        value={formData.achAccountHolderName}
                        onChange={(e) => handleChange('achAccountHolderName', e.target.value)}
                      />
                    </FormControl>
                  </FormField>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      label="Routing Number"
                      required
                      error={errors.achRoutingNumber}
                      description="9 digits"
                    >
                      <FormControl>
                        <Input
                          placeholder="123456789"
                          maxLength={9}
                          value={formData.achRoutingNumber}
                          onChange={(e) =>
                            handleChange('achRoutingNumber', e.target.value.replace(/\D/g, ''))
                          }
                        />
                      </FormControl>
                    </FormField>

                    <FormField
                      label="Account Number"
                      required
                      error={errors.achAccountNumber}
                      description="4-17 digits"
                    >
                      <FormControl>
                        <Input
                          placeholder="1234567890"
                          maxLength={17}
                          value={formData.achAccountNumber}
                          onChange={(e) =>
                            handleChange('achAccountNumber', e.target.value.replace(/\D/g, ''))
                          }
                        />
                      </FormControl>
                    </FormField>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField label="Account Type" required>
                      <Select
                        value={formData.achAccountType}
                        onValueChange={(value: 'checking' | 'savings') =>
                          handleChange('achAccountType', value)
                        }
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="checking">Checking</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>

                    <FormField label="Bank Name (Optional)">
                      <FormControl>
                        <Input
                          placeholder="Chase Bank"
                          value={formData.achBankName}
                          onChange={(e) => handleChange('achBankName', e.target.value)}
                        />
                      </FormControl>
                    </FormField>
                  </div>

                  {errors.general && (
                    <Alert variant="destructive">
                      <AlertDescription>{errors.general}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={returnToPersonalInfo}
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Address Verification Dialog */}
        <AddressVerificationDialog
          open={showAddressDialog}
          onOpenChange={setShowAddressDialog}
          verificationResult={addressVerificationResult}
          onAcceptSuggested={handleAcceptSuggestedAddress}
          onKeepOriginal={handleKeepOriginalAddress}
        />
      </div>
    </div>
  );
}
